export interface OverBudgetTask {
  task_id: string;
  name: string;
  estimated_hours: number;
  actual_hours: number | null;
  percent_complete: number;
  assigned_user_id: string | null;
}

export interface ProjectUserRate {
  id: string;
  project_id: string;
  user_id: string;
  bill_rate: number | null;
  cost_rate: number | null;
}

export interface Project {
  id?: string;
  company_id: string;
  number?: string;
  name: string;
  description?: string;
  entity_id?: string;
  contact_id?: string;
  opportunity_id?: string;
  status: 'Draft' | 'Active' | 'On Hold' | 'Completed' | 'Cancelled';
  billing_type: 'TimeAndMaterials' | 'PercentageComplete' | 'ByTaskPercentageComplete' | 'FixedPrice' | 'NonBillable';
  start_date?: string;
  due_date?: string;
  completed_date?: string;
  budget_hours: number;
  budget_amount: number;
  percent_complete: number;
  actual_hours?: number | null;
  manager_user_id?: string;
  location_id?: string;
  notes?: string;
  chat_thread_id?: string;
  entity_name?: string;
  default_labor_item_id?: string | null;
  default_material_item_id?: string | null;
  over_budget_tasks?: OverBudgetTask[];
  tasks?: ProjectTask[];
  milestones?: ProjectMilestone[];
}

export interface ProjectTask {
  id?: string;
  project_id: string;
  parent_task_id?: string;
  name: string;
  description?: string;
  status: 'Not Started' | 'In Progress' | 'Blocked' | 'Done';
  assigned_user_id?: string;
  estimated_hours: number;
  actual_hours?: number | null;
  billable: boolean;
  percent_complete: number;
  budget_amount?: number;
  start_date?: string;
  due_date?: string;
  completed_date?: string;
  sort_order: number;
  sub_tasks?: ProjectTask[];
}

export interface ProjectMilestone {
  id?: string;
  project_id: string;
  name: string;
  due_date?: string;
  completed_date?: string;
  invoice_on_completion: boolean;
  invoice_amount?: number;
  invoice_percent?: number;
  notes?: string;
}

export interface ProjectMaterial {
  id?: string;
  project_id: string;
  task_id?: string;
  item_id?: string;
  description: string;
  date: string;
  quantity: number;
  unit_cost: number;
  unit_price: number;
  billable: boolean;
  billed?: boolean;
  billing_event_id?: string;
  added_by_user_id: string;
  sort_order: number;
  line_total?: number;
}

export interface ProjectNote {
  id?: string;
  project_id: string;
  task_id?: string;
  user_id: string;
  note_date?: string;
  note: string;
}

export interface ProjectBillingEvent {
  id?: string;
  project_id: string;
  milestone_id?: string;
  task_id?: string;
  billing_date: string;
  billing_type?: string;
  percent_billed?: number;
  hours_billed?: number;
  materials_amount?: number;
  amount_billed: number;
  invoice_id?: string;
  invoiced?: boolean;
  invoiced_date?: string;
  notes?: string;
}

export interface TmLine {
  user_id: string;
  user_name: string;
  hours: number;
  effective_rate: number;
  amount_billed: number;
  description: string;
}

export interface BillingAssemblePreview {
  project_id: string;
  billing_type: string;
  percent_complete: number;
  percent_previously_billed: number;
  percent_billed?: number;
  time_entries: { id: string; user_id: string; task_id: string | null; entry_date: string; hours: number; task_name: string | null }[];
  total_hours: number;
  tm_lines: TmLine[] | null;
  materials: {
    id: string;
    item_id: string | null;
    description: string;
    date: string;
    quantity: number;
    unit_price: number;
    line_total: number
  }[];
  materials_total: number;
  amount_billed: number;
  note: string;
}

export interface ProjectDashboardSummary {
  summary: {
    total: number;
    active: number;
    on_hold: number;
    completed: number;
    draft: number;
    overdue: number;
    total_budget: number;
    total_budget_hours: number;
    avg_percent_complete: number;
    unbilled_materials: number;
    unbilled_hours: number;
  };
  upcoming_milestones: any[];
  recent_projects: any[];
}

export const PM_WORK_TYPES = [
  {value: 'regular', label: 'Regular', isPremium: false},
  {value: 'overtime', label: 'Overtime', isPremium: true},
  {value: 'after_hours', label: 'After Hours', isPremium: true},
  {value: 'holiday', label: 'Holiday', isPremium: true},
  {value: 'emergency', label: 'Emergency', isPremium: true},
] as const;

export type PmWorkType = typeof PM_WORK_TYPES[number]['value'];

export interface PmUserBillingRate {
  id: string;
  user_id: string;
  work_type: PmWorkType;
  base_rate: number;
  rate_modifier: number;
  effective_rate: number;
  active: boolean;
}

export interface ProjectKpis {
  project_id: string;
  number: string;
  name: string;
  status: string;
  billing_type: string;
  percent_complete: number;
  budget_amount: number;
  budget_hours: number;
  billed_amount: number;
  remaining_budget: number;
  total_tasks: number;
  done_tasks: number;
  blocked_tasks: number;
  total_estimated_hours: number;
  actual_hours: number;
  hours_variance: number;
  unbilled_materials: number;
  milestones: any[];
}
